import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Download, Share2, Copy, Mail, Check, ArrowUpRight, Clock, AlertTriangle, Zap, Brain, Lock, Loader2, X, LogIn } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { clearStatusCache, fetchProStatus, callProFeature, getUserId, isLoggedIn, loginWithGoogle } from "@/lib/subscription";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeIn = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }
};

export default function DecisionMapPage() {
  const navigate = useNavigate();
  const { sessionId } = useParams();
  const [decisionMap, setDecisionMap] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareLink, setShareLink] = useState("");
  const [isGeneratingShare, setIsGeneratingShare] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [saved, setSaved] = useState(false);
  
  // Pro features state
  const [proStatus, setProStatus] = useState(null);
  const [proStatusLoading, setProStatusLoading] = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState("");
  
  // Pro feature results
  const [challengeResult, setChallengeResult] = useState(null);
  const [deepReasoningResult, setDeepReasoningResult] = useState(null);
  const [featureLoading, setFeatureLoading] = useState(null); // "challenge" or "deep_reasoning"

  useEffect(() => {
    // Fetch pro status
    fetchProStatus(true).then(status => {
      setProStatus(status);
      setProStatusLoading(false);
    }).catch(() => {
      setProStatusLoading(false);
    });
  }, []);

  useEffect(() => {
    const loadDecisionMap = async () => {
      try {
        const response = await axios.get(`${API}/decision/${sessionId}/map`);
        setDecisionMap(response.data);
        
        // Clear status cache (server already tracked the decision)
        clearStatusCache();
        
        // Save to history (localStorage)
        saveToHistory(response.data);
      } catch (err) {
        if (err.response?.status === 400) {
          navigate(`/think/${sessionId}`);
          return;
        }
        toast.error("Unable to load decision map");
      } finally {
        setIsLoading(false);
      }
    };
    loadDecisionMap();
  }, [sessionId, navigate]);

  const saveToHistory = (data) => {
    try {
      const existing = JSON.parse(localStorage.getItem("decisora_history") || "[]");
      // Check if already exists
      if (!existing.find(h => h.session_id === data.session_id)) {
        const historyItem = {
          session_id: data.session_id,
          decision_text: data.decision_text,
          domain: data.domain,
          framework: data.framework,
          confidence_score: data.confidence_score,
          timestamp: new Date().toISOString()
        };
        existing.unshift(historyItem);
        // Keep only last 20
        const trimmed = existing.slice(0, 20);
        localStorage.setItem("decisora_history", JSON.stringify(trimmed));
        setSaved(true);
      }
    } catch (e) {
      console.error("Failed to save history");
    }
  };

  const handleDownloadPDF = async () => {
    setIsDownloading(true);
    try {
      const response = await axios.get(`${API}/decision/${sessionId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `decision-map-${sessionId.slice(0, 8)}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success("Downloaded");
    } catch {
      toast.error("Download failed");
    } finally {
      setIsDownloading(false);
    }
  };

  const handleShare = async () => {
    setShowShareDialog(true);
    setIsGeneratingShare(true);
    try {
      const response = await axios.post(`${API}/decision/${sessionId}/share`);
      setShareLink(response.data.share_url);
    } catch {
      toast.error("Failed to generate link");
    } finally {
      setIsGeneratingShare(false);
    }
  };

  const handleCopyLink = async () => {
    await navigator.clipboard.writeText(shareLink);
    toast.success("Copied");
  };

  const handleEmailShare = () => {
    const subject = encodeURIComponent("Decision Map");
    const body = encodeURIComponent(`View my decision analysis:\n\n${shareLink}`);
    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
  };

  const handleProFeature = async (feature) => {
    // Check if user needs to upgrade or sign in
    if (proStatus?.needs_payment || proStatus?.trial_used) {
      // Trial used - check if user is logged in
      if (!isLoggedIn()) {
        // Not logged in - show sign-in modal
        setShowSignInModal(true);
        return;
      }
      // Logged in but needs payment - show upgrade modal
      setUpgradeMessage(
        proStatus?.trial_used 
          ? "Your free trial is complete. Upgrade to unlock these features."
          : "Please upgrade to access pro features."
      );
      setShowUpgradeModal(true);
      return;
    }
    
    setFeatureLoading(feature);
    try {
      const result = await callProFeature(sessionId, feature);
      
      if (!result.success) {
        // Trial was already used or needs payment
        setProStatus(prev => ({
          ...prev,
          trial_used: result.trial_used,
          needs_payment: result.needs_payment
        }));
        
        // Check if user is logged in
        if (!isLoggedIn()) {
          setShowSignInModal(true);
        } else {
          setUpgradeMessage(result.message || "Please upgrade to continue using pro features.");
          setShowUpgradeModal(true);
        }
        return;
      }
      
      // Success - update state
      if (feature === "challenge") {
        setChallengeResult(result.result);
      } else if (feature === "deep_reasoning") {
        setDeepReasoningResult(result.result);
      }
      
      // Update pro status
      setProStatus(prev => ({
        ...prev,
        trial_used: result.trial_used,
        needs_payment: result.needs_payment
      }));
      
      // After trial used, show sign-in prompt for anonymous users
      if (result.trial_used && result.needs_payment) {
        setTimeout(() => {
          if (!isLoggedIn()) {
            setShowSignInModal(true);
          } else {
            setUpgradeMessage("You've used your free trial. Upgrade to continue exploring pro features.");
            setShowUpgradeModal(true);
          }
        }, 2000);
      }
      
    } catch (error) {
      console.error("Pro feature error:", error);
      toast.error("Failed to run analysis. Please try again.");
    } finally {
      setFeatureLoading(null);
    }
  };

  const handleUpgrade = () => {
    navigate("/pricing");
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!decisionMap) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-background">
        <p className="text-muted-foreground">Unable to load</p>
        <Button variant="outline" size="sm" onClick={() => navigate("/")}>Go home</Button>
      </div>
    );
  }

  const riskClass = {
    Low: "risk-low",
    Medium: "risk-medium",
    High: "risk-high"
  }[decisionMap.risk_level] || "risk-medium";

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border/50">
        <div className="max-w-3xl mx-auto px-6 h-14 flex items-center justify-between">
          <button
            data-testid="back-btn"
            onClick={() => navigate("/")}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-2">
            <Button
              data-testid="download-pdf-btn"
              variant="ghost"
              size="sm"
              onClick={handleDownloadPDF}
              disabled={isDownloading}
              className="h-8 px-3 text-muted-foreground hover:text-foreground"
            >
              <Download className="h-4 w-4" />
            </Button>
            <Button
              data-testid="share-btn"
              variant="ghost"
              size="sm"
              onClick={handleShare}
              className="h-8 px-3 text-muted-foreground hover:text-foreground"
            >
              <Share2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Success indicator */}
      <div className="border-b border-border/30">
        <div className="max-w-3xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs text-primary">
            <Check className="h-3.5 w-3.5" />
            <span>Structured thinking completed</span>
          </div>
          {saved && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Clock className="h-3 w-3" />
              <span>Decision saved</span>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-6 py-10">
        <div className="space-y-8">

          {/* Quick Summary Block */}
          {decisionMap.option_a && decisionMap.confidence_score && (
            <motion.div {...fadeIn} className="p-4 rounded-xl border border-border/60 bg-card/50" data-testid="quick-summary">
              <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
                <span className="text-muted-foreground">
                  <span className="mr-1.5">🔍</span>Recommended: <span className="text-foreground font-medium">{decisionMap.option_a}</span>
                </span>
                <span className="text-muted-foreground">
                  <span className="mr-1.5">📊</span>Confidence: <span className="text-foreground font-medium">{decisionMap.confidence_score}/100</span>
                </span>
                {decisionMap.risk_level && (
                  <span className="text-muted-foreground">
                    <span className="mr-1.5">⚠️</span>Risk: <span className="text-foreground font-medium">{decisionMap.risk_level}</span>
                  </span>
                )}
              </div>
            </motion.div>
          )}
          
          {/* Decision Summary */}
          {decisionMap.decision_summary && (
            <motion.div {...fadeIn} className="p-5 rounded-xl glow-card">
              <p className="text-foreground leading-relaxed" data-testid="decision-summary">
                {decisionMap.decision_summary}
              </p>
            </motion.div>
          )}

          {/* 1. Importance Trigger */}
          <motion.div {...fadeIn} transition={{ delay: 0.01 }} className="space-y-1.5 py-2" data-testid="importance-trigger">
            <p className="text-sm font-medium text-foreground">This is not a small decision.</p>
            <p className="text-sm text-muted-foreground">This choice can impact your next few years.</p>
            <p className="text-xs text-muted-foreground/70 italic">You've seen the core clarity. Deeper layers still exist.</p>
          </motion.div>

          {/* Decision & Framework */}
          <motion.div {...fadeIn} transition={{ delay: 0.02 }} className="space-y-3">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Your Decision
            </p>
            <h1 className="font-heading text-xl md:text-2xl text-foreground leading-snug">
              {decisionMap.decision_text}
            </h1>
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <span>{decisionMap.framework}</span>
              {decisionMap.framework_justification && (
                <>
                  <span className="text-border">•</span>
                  <span className="text-xs">{decisionMap.framework_justification}</span>
                </>
              )}
            </div>
          </motion.div>

          {/* Options Comparison */}
          <motion.div {...fadeIn} transition={{ delay: 0.04 }} className="space-y-4">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Options Compared
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="floating-card p-5 space-y-3">
                <div className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-md bg-primary/20 flex items-center justify-center text-xs font-medium text-primary">A</span>
                </div>
                <p className="text-foreground font-medium" data-testid="option-a">
                  {decisionMap.option_a}
                </p>
                {decisionMap.option_a_tradeoffs && (
                  <p className="text-sm text-muted-foreground">
                    {decisionMap.option_a_tradeoffs}
                  </p>
                )}
              </div>
              
              <div className="floating-card p-5 space-y-3">
                <div className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-md bg-secondary flex items-center justify-center text-xs font-medium text-secondary-foreground">B</span>
                </div>
                <p className="text-foreground font-medium" data-testid="option-b">
                  {decisionMap.option_b}
                </p>
                {decisionMap.option_b_tradeoffs && (
                  <p className="text-sm text-muted-foreground">
                    {decisionMap.option_b_tradeoffs}
                  </p>
                )}
              </div>
            </div>
          </motion.div>

          {/* Risk & Confidence */}
          <motion.div {...fadeIn} transition={{ delay: 0.06 }} className="grid md:grid-cols-2 gap-4">
            {decisionMap.risk_level && (
              <div className="floating-card p-5 space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Risk Level</p>
                  <AlertTriangle className="h-4 w-4 text-muted-foreground/50" />
                </div>
                <div className={`inline-block px-3 py-1.5 rounded-lg text-sm font-medium ${riskClass}`} data-testid="risk-level">
                  {decisionMap.risk_level}
                </div>
                {decisionMap.risk_explanation && (
                  <p className="text-xs text-muted-foreground">{decisionMap.risk_explanation}</p>
                )}
              </div>
            )}
            {decisionMap.confidence_score && (
              <div className="floating-card p-5 space-y-2">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Confidence</p>
                <p className="text-2xl font-semibold text-foreground" data-testid="confidence-score">
                  {decisionMap.confidence_score}<span className="text-sm text-muted-foreground font-normal">/100</span>
                </p>
                {decisionMap.confidence_explanation && (
                  <p className="text-xs text-muted-foreground">{decisionMap.confidence_explanation}</p>
                )}
              </div>
            )}
          </motion.div>

          <div className="h-px bg-border/50" />

          {/* Clarity Statement */}
          {decisionMap.clarity_statement && (
            <motion.div {...fadeIn} transition={{ delay: 0.08 }} className="space-y-3">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                What Your Analysis Reveals
              </p>
              <p className="text-lg text-foreground leading-relaxed" data-testid="clarity-statement">
                {decisionMap.clarity_statement}
              </p>
            </motion.div>
          )}

          {/* Key Trade-offs */}
          {decisionMap.key_insights && decisionMap.key_insights.length > 0 && (
            <motion.div {...fadeIn} transition={{ delay: 0.1 }} className="space-y-4">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Trade-offs From Your Reasoning
              </p>
              <ul className="space-y-3" data-testid="key-insights">
                {decisionMap.key_insights.map((insight, index) => (
                  <li key={index} className="flex items-start gap-3 text-foreground">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                    <span className="leading-relaxed">{insight}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          )}

          {/* Curiosity trigger after trade-offs */}
          <p className="text-xs text-muted-foreground/60 italic">Some trade-offs are not immediately obvious.</p>

          {/* What Would Change This */}
          {decisionMap.what_would_change && (
            <motion.div {...fadeIn} transition={{ delay: 0.12 }} className="p-5 rounded-xl bg-secondary/30 border border-border/40">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                What Would Change This Decision?
              </p>
              <p className="text-foreground leading-relaxed" data-testid="what-would-change">
                {decisionMap.what_would_change}
              </p>
            </motion.div>
          )}

          {/* Practical Steps */}
          {decisionMap.practical_steps && decisionMap.practical_steps.length > 0 && (
            <motion.div {...fadeIn} transition={{ delay: 0.14 }} className="glow-card p-6 space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-xs font-medium text-primary uppercase tracking-wider">
                  Practical Next Actions (7 Days)
                </p>
                <ArrowUpRight className="h-4 w-4 text-primary/50" />
              </div>
              <ul className="space-y-3" data-testid="practical-steps">
                {decisionMap.practical_steps.map((step, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="w-5 h-5 rounded-md bg-primary/20 flex items-center justify-center text-xs font-medium text-primary flex-shrink-0 mt-0.5">
                      {index + 1}
                    </span>
                    <span className="text-foreground leading-relaxed">{step}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          )}

          {/* Long-term View */}
          {decisionMap.long_term_view && (
            <motion.div {...fadeIn} transition={{ delay: 0.16 }} className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Long-term View (5-10 Years)
                </p>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary/70">Advanced Layer Available</span>
              </div>
              <p className="text-foreground leading-relaxed" data-testid="long-term-view">
                {decisionMap.long_term_view}
              </p>
              <p className="text-[11px] text-muted-foreground/50 italic">
                This is a surface-level summary. Full depth includes deeper risk patterns and long-term consequences.
              </p>
            </motion.div>
          )}

          {/* Curiosity trigger */}
          <p className="text-xs text-muted-foreground/60 italic">There may be second-order effects you haven't explored.</p>

          {/* Closing Note */}
          <motion.div {...fadeIn} transition={{ delay: 0.18 }} className="pt-4 border-t border-border/30">
            <p className="text-sm text-muted-foreground italic leading-relaxed" data-testid="closing-note">
              {decisionMap.closing_note || "This analysis reflects your own reasoning. It doesn't tell you what to do—it shows which path aligns more consistently with your thinking."}
            </p>
          </motion.div>

          {/* Pro Features Section */}
          <motion.div {...fadeIn} transition={{ delay: 0.2 }} className="space-y-4 pt-4">
            <div className="space-y-1">
              <p className="text-sm font-medium text-foreground">
                Go one level deeper before deciding
              </p>
              <p className="text-xs text-muted-foreground">
                Most people stop at clarity. Better decisions come from deeper analysis.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4">
              {/* Challenge My Choice Button */}
              <div className="floating-card p-5 space-y-3">
                <div className="flex items-center gap-2">
                  <Zap className="h-4 w-4 text-amber-500" />
                  <span className="text-sm font-medium text-foreground">Challenge My Choice</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Stress-test your decision. See what could go wrong before you commit.
                </p>
                {!challengeResult ? (
                  <Button
                    data-testid="challenge-my-choice-btn"
                    variant={proStatus?.trial_used ? "outline" : "default"}
                    size="sm"
                    className="w-full h-9 text-sm"
                    disabled={featureLoading === "challenge" || proStatusLoading}
                    onClick={() => handleProFeature("challenge")}
                  >
                    {featureLoading === "challenge" ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Analyzing...
                      </>
                    ) : proStatus?.needs_payment ? (
                      <>
                        <Lock className="h-4 w-4 mr-2" />
                        Upgrade to continue
                      </>
                    ) : proStatus?.trial_used ? (
                      <>
                        <Lock className="h-4 w-4 mr-2" />
                        Upgrade to continue
                      </>
                    ) : (
                      "Test my decision (Free once)"
                    )}
                  </Button>
                ) : (
                  <div className="text-xs text-primary">✓ Analysis complete</div>
                )}
              </div>

              {/* Deep Reasoning Button */}
              <div className="floating-card p-5 space-y-3">
                <div className="flex items-center gap-2">
                  <Brain className="h-4 w-4 text-purple-500" />
                  <span className="text-sm font-medium text-foreground">Deep Reasoning</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Explore long-term consequences, hidden trade-offs, and deeper patterns.
                </p>
                {!deepReasoningResult ? (
                  <Button
                    data-testid="deep-reasoning-btn"
                    variant={proStatus?.trial_used ? "outline" : "default"}
                    size="sm"
                    className="w-full h-9 text-sm"
                    disabled={featureLoading === "deep_reasoning" || proStatusLoading}
                    onClick={() => handleProFeature("deep_reasoning")}
                  >
                    {featureLoading === "deep_reasoning" ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Analyzing...
                      </>
                    ) : proStatus?.needs_payment ? (
                      <>
                        <Lock className="h-4 w-4 mr-2" />
                        Upgrade to continue
                      </>
                    ) : proStatus?.trial_used ? (
                      <>
                        <Lock className="h-4 w-4 mr-2" />
                        Upgrade to continue
                      </>
                    ) : (
                      "Go deeper (Free once)"
                    )}
                  </Button>
                ) : (
                  <div className="text-xs text-primary">✓ Analysis complete</div>
                )}
              </div>
            </div>

            {/* Authority + Trust Line */}
            <p className="text-[11px] text-muted-foreground/60 text-center pt-1">
              Used for serious career, money, and life decisions.
            </p>
            <AnimatePresence>
              {challengeResult && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="glow-card p-5 space-y-4"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Zap className="h-4 w-4 text-amber-500" />
                      <span className="text-sm font-medium text-foreground">Devil's Advocate Analysis</span>
                    </div>
                    <button 
                      onClick={() => setChallengeResult(null)} 
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  
                  {challengeResult.blind_spots?.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-amber-500 uppercase tracking-wider">Blind Spots</p>
                      <ul className="space-y-1">
                        {challengeResult.blind_spots.map((item, i) => (
                          <li key={i} className="text-sm text-foreground flex items-start gap-2">
                            <span className="text-amber-500 mt-1">•</span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {challengeResult.counter_arguments?.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-orange-500 uppercase tracking-wider">Counter Arguments</p>
                      <ul className="space-y-1">
                        {challengeResult.counter_arguments.map((item, i) => (
                          <li key={i} className="text-sm text-foreground flex items-start gap-2">
                            <span className="text-orange-500 mt-1">•</span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {challengeResult.hidden_risks?.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-red-500 uppercase tracking-wider">Hidden Risks</p>
                      <ul className="space-y-1">
                        {challengeResult.hidden_risks.map((item, i) => (
                          <li key={i} className="text-sm text-foreground flex items-start gap-2">
                            <span className="text-red-500 mt-1">•</span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {challengeResult.key_question && (
                    <div className="p-3 bg-secondary/50 rounded-lg">
                      <p className="text-xs font-medium text-primary uppercase tracking-wider mb-1">Key Question</p>
                      <p className="text-sm text-foreground">{challengeResult.key_question}</p>
                    </div>
                  )}
                  
                  {challengeResult.recommendation && (
                    <p className="text-sm text-muted-foreground italic">{challengeResult.recommendation}</p>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Deep Reasoning Result */}
            <AnimatePresence>
              {deepReasoningResult && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="glow-card p-5 space-y-4"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Brain className="h-4 w-4 text-purple-500" />
                      <span className="text-sm font-medium text-foreground">Deep Reasoning Analysis</span>
                    </div>
                    <button 
                      onClick={() => setDeepReasoningResult(null)} 
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  
                  {deepReasoningResult.long_term_implications?.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-purple-500 uppercase tracking-wider">Long-term Implications (5-10 Years)</p>
                      <ul className="space-y-1">
                        {deepReasoningResult.long_term_implications.map((item, i) => (
                          <li key={i} className="text-sm text-foreground flex items-start gap-2">
                            <span className="text-purple-500 mt-1">•</span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {deepReasoningResult.ripple_effects?.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-blue-500 uppercase tracking-wider">Ripple Effects</p>
                      <ul className="space-y-1">
                        {deepReasoningResult.ripple_effects.map((item, i) => (
                          <li key={i} className="text-sm text-foreground flex items-start gap-2">
                            <span className="text-blue-500 mt-1">•</span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {deepReasoningResult.alternative_paths?.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-green-500 uppercase tracking-wider">Alternative Paths</p>
                      <ul className="space-y-1">
                        {deepReasoningResult.alternative_paths.map((item, i) => (
                          <li key={i} className="text-sm text-foreground flex items-start gap-2">
                            <span className="text-green-500 mt-1">•</span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {deepReasoningResult.critical_assumptions?.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-amber-500 uppercase tracking-wider">Critical Assumptions</p>
                      <ul className="space-y-1">
                        {deepReasoningResult.critical_assumptions.map((item, i) => (
                          <li key={i} className="text-sm text-foreground flex items-start gap-2">
                            <span className="text-amber-500 mt-1">•</span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {deepReasoningResult.time_sensitivity && (
                    <div className="p-3 bg-secondary/50 rounded-lg">
                      <p className="text-xs font-medium text-primary uppercase tracking-wider mb-1">Time Sensitivity</p>
                      <p className="text-sm text-foreground">{deepReasoningResult.time_sensitivity}</p>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Actions */}
          <motion.div {...fadeIn} transition={{ delay: 0.22 }} className="flex items-center justify-center gap-3 pt-6">
            <Button
              data-testid="start-new-btn"
              onClick={() => navigate("/")}
              variant="outline"
              className="h-10 px-6 rounded-full text-sm"
            >
              New decision
            </Button>
          </motion.div>

          {/* 7. Final Conversion Push */}
          {proStatus && !proStatus.can_use && proStatus.needs_payment && (
            <motion.div {...fadeIn} transition={{ delay: 0.24 }} className="text-center space-y-3 pt-4" data-testid="conversion-push">
              <p className="text-sm text-muted-foreground">
                If this decision truly matters, don't stop at surface clarity.
              </p>
              <Button
                onClick={() => navigate("/pricing")}
                className="h-10 px-8 rounded-full text-sm"
                data-testid="unlock-clarity-btn"
              >
                Unlock Full Decision Clarity
              </Button>
              <p className="text-[11px] text-muted-foreground/50">
                One deeper layer can change your decision.
              </p>
            </motion.div>
          )}

          {/* 8. Exit Interruption (Soft, static) */}
          <motion.div
            {...fadeIn}
            transition={{ delay: 0.26 }}
            className="text-center py-4"
            data-testid="exit-interruption"
          >
            <p className="text-xs text-muted-foreground/40 italic">
              Before you finalize this decision, consider exploring one level deeper.
            </p>
          </motion.div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border/30 py-8 px-6">
        <div className="max-w-3xl mx-auto text-center space-y-3">
          <p className="text-xs text-muted-foreground">
            Clarity &gt; Comfort. DECISORA is a decision analyst, not a motivational speaker.
          </p>
          <p className="text-[11px] text-muted-foreground/50">
            Decisora provides structured thinking tools, not professional advice.
          </p>
        </div>
      </footer>

      {/* Share Dialog */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent className="sm:max-w-sm bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-foreground">Share</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            {isGeneratingShare ? (
              <div className="flex justify-center py-6">
                <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : shareLink ? (
              <>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    readOnly
                    value={shareLink}
                    className="flex-1 px-3 py-2 text-xs bg-secondary border border-border rounded-lg text-foreground"
                    data-testid="share-link-input"
                  />
                  <Button
                    data-testid="copy-link-btn"
                    variant="outline"
                    size="sm"
                    onClick={handleCopyLink}
                    className="h-9 px-3"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <Button
                  data-testid="email-share-btn"
                  variant="outline"
                  className="w-full h-9 text-sm"
                  onClick={handleEmailShare}
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Email
                </Button>
              </>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>

      {/* Upgrade Modal */}
      <Dialog open={showUpgradeModal} onOpenChange={setShowUpgradeModal}>
        <DialogContent className="sm:max-w-sm bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-foreground flex items-center gap-2">
              <Lock className="h-5 w-5 text-primary" />
              Unlock Pro Features
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-muted-foreground">
              {upgradeMessage}
            </p>
            <div className="p-3 bg-secondary/50 rounded-lg space-y-2">
              <p className="text-xs font-medium text-foreground">Pro features include:</p>
              <ul className="text-xs text-muted-foreground space-y-1">
                <li className="flex items-center gap-2">
                  <Zap className="h-3 w-3 text-amber-500" />
                  Challenge My Choice (Devil's Advocate)
                </li>
                <li className="flex items-center gap-2">
                  <Brain className="h-3 w-3 text-purple-500" />
                  Deep Reasoning Analysis
                </li>
              </ul>
            </div>
            <div className="flex gap-2">
              <Button
                data-testid="upgrade-modal-btn"
                className="flex-1 h-10"
                onClick={handleUpgrade}
              >
                View Plans
              </Button>
              <Button
                variant="outline"
                className="h-10"
                onClick={() => setShowUpgradeModal(false)}
              >
                Later
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Sign In Modal - shown when trial used and user not logged in */}
      <Dialog open={showSignInModal} onOpenChange={setShowSignInModal}>
        <DialogContent className="sm:max-w-sm bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-foreground flex items-center gap-2">
              <LogIn className="h-5 w-5 text-primary" />
              Sign in to Continue
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-muted-foreground">
              You've used your free trial. Sign in to unlock pro features or upgrade your plan.
            </p>
            <div className="p-3 bg-secondary/50 rounded-lg space-y-2">
              <p className="text-xs font-medium text-foreground">Pro features include:</p>
              <ul className="text-xs text-muted-foreground space-y-1">
                <li className="flex items-center gap-2">
                  <Zap className="h-3 w-3 text-amber-500" />
                  Challenge My Choice (Devil's Advocate)
                </li>
                <li className="flex items-center gap-2">
                  <Brain className="h-3 w-3 text-purple-500" />
                  Deep Reasoning Analysis
                </li>
              </ul>
            </div>
            <div className="flex gap-2">
              <Button
                data-testid="signin-modal-btn"
                className="flex-1 h-10"
                onClick={loginWithGoogle}
              >
                <LogIn className="h-4 w-4 mr-2" />
                Sign in with Google
              </Button>
              <Button
                variant="outline"
                className="h-10"
                onClick={() => setShowSignInModal(false)}
              >
                Later
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
